import {
    LitElement,
    css
} from "lit-element";
export * from "lit-element";

const animations = css `
@keyframes slideInTop {
    0% {
        transform: translate3d(0, -100%, 0);
    }

    100% {
        transform: translate3d(0, 0, 0);
    }
}

@keyframes slideInLeft {
    0% {
        transform: translate3d(-100%, 0, 0);
    }

    100% {
        transform: translate3d(0, 0, 0);
    }
}

@keyframes slideOutLeft {
    0% {
        transform: translate3d(0, 0, 0);
    }

    100% {
        transform: translate3d(-100%, 0, 0);
    }
}

@keyframes slideInRight {
    0% {
        transform: translate3d(100%, 0, 0);
    }

    100% {
        transform: translate3d(0, 0, 0);
    }
}

@keyframes slideOutRight {
    0% {
        transform: translate3d(0, 0, 0);
    }

    100% {
        transform: translate3d(100%, 0, 0);
    }
}

@keyframes slideInBottom {
    0% {
        transform: translate3d(0, 100%, 0);
    }

    100% {
        transform: translate3d(0, 0, 0);
    }
}

@keyframes fadeIn {
    0% {
        opacity: 0;
    }

    100% {
        opacity: 1;
    }
}

@keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translate3d(0, 100%, 0);
    }
  
    to {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
  } 

@keyframes fadeInLeft {
    0% {
        transform: translate3d(-100%, 0, 0);
        opacity: 0;
    }

    100% {
        transform: translate3d(0, 0, 0);
        opacity: 1;
    }
}

@keyframes rotating {
    100% {
        transform: rotate(360deg);
    }
}
`

// Helper functions

/**
 * @description
 * Determines whether a slot with the given name exists in an element.
 *
 * ### Properties
 * @arg - element - the custom element (most time its the host).
 * @arg - name - name of the slot.
 *
 */
export const hasSlot = (element, name) => {
    return [...element.querySelectorAll('[slot]')].filter((slottedEl) => slottedEl.slot === name).length > 0;
}


/**
 * @description
 * Limits the max amount of calls that can be called.
 *
 * ### Properties
 * @arg - func - callback function
 * @arg - limit - time it takes for that it can be called.
 *
 */
export const throttleFn = (func, limit) => {
    let lastFunc
    let lastRan
    return function () {
        const context = this
        const args = arguments
        if (!lastRan) {
            func.apply(context, args)
            lastRan = Date.now()
        } else {
            clearTimeout(lastFunc)
            lastFunc = setTimeout(function () {
                if ((Date.now() - lastRan) >= limit) {
                    func.apply(context, args)
                    lastRan = Date.now()
                }
            }, limit - (Date.now() - lastRan))
        }
    }
}

/**
 * @description
 * Executes the function later so it doesn't fill the event callstack.
 *
 * ### Properties
 * @arg - fn - callback function
 *
 */
export const debounce = (fn) => {
    let timer;

    return function () {
        const context = this;
        const args = arguments;

        if (timer) {
            window.cancelAnimationFrame(timer);
        }

        timer = window.requestAnimationFrame(() => {
            fn.apply(context, args);
        });
    };
}


/**
 * @description
 * Base class for all components.
 *
 * ### Properties
 * @prop - {String} horizontalContentPosition - Position of the content horizontally (left, center, right).
 * @prop - {String} verticalContentPosition - Position of the content vertically (top, center, bottm).
 *
 */
export class TrueBase extends LitElement {
    static get properties() {
        return {
            horizontalContentPosition: {
                type: String,
                reflect: true
            },
            verticalContentPosition: {
                type: String,
                reflect: true
            },
            animation: {
                type: String
            },
            inView: {
                type: Boolean
            },
            io: {
                type: IntersectionObserver
            }
        };
    }

    constructor() {
        super();
        this.addIntersectionObserver(this);
    }

    addIntersectionObserver(el) {
        this.io = new IntersectionObserver((data) => {
            if (data[0].isIntersecting) {
                el.setAttribute('inView', "");
                this.removeIntersectionObserver();
            }
        });
        this.io.observe(el);
    }

    removeIntersectionObserver() {
        if (this.io) {
            this.io.disconnect();
            this.io = null;
        }
    }

    static get styles() {
        return [css `
            :host([horizontalContentPosition="left"]) .tp-content-wrapper {
                justify-content: flex-start;
                display: flex;
            }

            :host([horizontalContentPosition="center"]) .tp-content-wrapper {
                justify-content: center;
                display: flex;
            }

            :host([horizontalContentPosition="right"]) .tp-content-wrapper {
                justify-content: flex-end;
                display: flex;
            }

            :host([verticalContentPosition="top"]) .tp-content-wrapper {
                align-items: flex-start;
                display: flex;
            }

            :host([verticalContentPosition="center"]) .tp-content-wrapper {
                align-items: center;
                display: flex;
            }

            :host([verticalContentPosition="bottom"]) .tp-content-wrapper {
                align-items: flex-end;
                display: flex;
            }

            /* .tp-content-wrapper {
                height: 100%;
            } */

            :host([animation]) {
                animation-duration: var(--animation-duration, 1s);
                animation-timing-function: var(--animation-timing-function, ease);
                animation-delay: var(--animation-delay, 0s);
                animation-iteration-count: var(--animation-iteration-count, 1);
                animation-direction: var(--animation-direction, normal);
                animation-fill-mode: var(--animation-fill-mode, none);
            }

            :host([animation="fadeIn"][inview]) {
                animation-name: fadeIn;
            }

            :host([animation="fadeInUp"][inview]) {
                animation-name: fadeInUp;
            }

            :host([animation="slideInTop"][inview]) {
                animation: slideInTop .6s ease;
            }

        `, animations]
    }
}